import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, X } from "lucide-react";
import { mfaApi } from "../../api/mfa";
import { getUserFromToken } from "../../utils/utils";

export const MFA_NUDGE_DISMISS_KEY = "mfaNudgeDismissed";

// Dispatched wherever MFA gets enabled/disabled (MfaSetupModal,
// MfaDisableModal) so the banner - mounted once in AppLayout and never
// remounted by in-app navigation - finds out its status is stale instead
// of only ever checking once per login.
export const MFA_STATUS_CHANGED_EVENT = "mfa-status-changed";

/**
 * Dismissible nudge shown across the app when the logged-in user hasn't
 * enabled MFA - same pattern GitHub/AWS/Google use. Dismissal is
 * session-scoped (sessionStorage), so it reappears on the next fresh
 * login rather than being silenced forever by one click.
 */
const MfaNudgeBanner: React.FC = () => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const checkStatus = () => {
      if (sessionStorage.getItem(MFA_NUDGE_DISMISS_KEY) === "true") {
        setVisible(false);
        return;
      }

      const userId = getUserFromToken();
      if (!userId) return;

      mfaApi
        .getStatus(userId)
        .then((res) => {
          setVisible(!res.data.mfaEnabled);
        })
        .catch(() => {
          // Fail silently - don't nag if we can't confirm status either way.
        });
    };

    checkStatus();
    window.addEventListener(MFA_STATUS_CHANGED_EVENT, checkStatus);
    return () => window.removeEventListener(MFA_STATUS_CHANGED_EVENT, checkStatus);
  }, []);

  const handleDismiss = () => {
    sessionStorage.setItem(MFA_NUDGE_DISMISS_KEY, "true");
    setVisible(false);
  };

  const handleEnable = () => {
    navigate("/security");
  };

  if (!visible) return null;

  return (
    <div className="bg-blue-50 border-b border-blue-200 px-4 py-2.5 flex items-center justify-between gap-4">
      <div className="flex items-center gap-2 text-sm text-blue-900">
        <ShieldAlert className="w-4 h-4 flex-shrink-0" />
        <span>
          Two-factor authentication isn't enabled on your account. Add an
          extra layer of security to protect it.
        </span>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        <button
          type="button"
          onClick={handleEnable}
          className="text-sm font-medium text-blue-700 hover:text-blue-900 underline"
        >
          Enable now
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          className="text-blue-400 hover:text-blue-600"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default MfaNudgeBanner;
